import React, { useState, useEffect } from 'react'
import { logo, ride1 } from '../../../assets/index'
import Button from '../../templates/Button';
import { useDispatch, useSelector } from 'react-redux'
import InputField from '../../templates/InputField'
import { userLogin } from '../../../redux_store/auth/auth_thunk';
import { FAIL, LOADING, SUCCESS } from '../../../redux_store/actionTypes';
import LoadingModal from '../../templates/modals/LoadingModal';
import toast from '../../templates/ToastMessage'
import { useNavigate } from 'react-router-dom'
import { clearAuthState } from '../../../redux_store/auth/auth_slice';
import { ArrowRight, Lock, Mail } from 'iconoir-react';

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigator = useNavigate();
  const { status, message, authState } = useSelector((state) => state.auth);
  const [credentials, setCredentials] = useState({
    email: '', password: ''
  });

  useEffect(() => {
    if (status === FAIL) {
      toast({ message, status });
    } else if (status === SUCCESS && authState === 'LOGIN') {
      toast({ message, status });
      dispatch(clearAuthState());
      navigator('/analytics');
    }
    // eslint-disable-next-line
  }, [status, authState]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentials({ ...credentials, [name]: value })
  }

  const handleFormSubmit = (e) => {
    e.preventDefault();
    dispatch(userLogin(credentials));
  }

  return (
    <div className='min-h-screen flex'>
      {status === LOADING && <LoadingModal message={'Signing you in...'} />}

      {/* Left side - Hero Image */}
      <div className='hidden lg:flex lg:w-3/5 relative overflow-hidden'>
        {/* Background image with overlay */}
        <img
          alt='bg-img'
          src={ride1}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-primary-800/80 to-primary-900/90" />

        {/* Decorative elements */}
        <div className="absolute inset-0 bg-hero-pattern opacity-10" />

        {/* Floating shapes */}
        <div className="absolute top-20 left-20 w-64 h-64 bg-accent-400/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-primary-400/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }} />

        {/* Content */}
        <div className='relative z-10 flex flex-col justify-center px-16 text-white'>
          <div className="mb-8">
            <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-6">
              <img alt='logo' src={logo} className="w-10 h-10 object-contain" />
            </div>
            <h1 className='text-5xl font-bold leading-tight mb-4'>
              Welcome to<br />
              <span className="text-accent-400">WayRide</span> Admin
            </h1>
            <p className='text-lg text-primary-100 max-w-md'>
              Manage your fleet, monitor trips, and deliver exceptional ride experiences from one powerful dashboard.
            </p>
          </div>

          {/* Stats */}
          <div className="flex gap-8 mt-8">
            <div>
              <p className="text-4xl font-bold text-white">10K+</p>
              <p className="text-sm text-primary-200">Active Riders</p>
            </div>
            <div className="w-px bg-primary-400/30" />
            <div>
              <p className="text-4xl font-bold text-white">500+</p>
              <p className="text-sm text-primary-200">Daily Trips</p>
            </div>
            <div className="w-px bg-primary-400/30" />
            <div>
              <p className="text-4xl font-bold text-white">99%</p>
              <p className="text-sm text-primary-200">Uptime</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className='flex-1 flex items-center justify-center p-8 bg-white'>
        <div className='w-full max-w-md'>
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-glow">
              <img alt='logo' src={logo} className="w-10 h-10 object-contain" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center lg:text-left mb-10">
            <h2 className='text-3xl font-bold text-surface-800 mb-2'>Sign in</h2>
            <p className='text-surface-500'>Enter your credentials to access the dashboard</p>
          </div>

          {/* Form */}
          <form onSubmit={handleFormSubmit} className='space-y-6'>
            <div className='space-y-2'>
              <label className='text-sm font-semibold text-surface-700'>Email Address</label>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400">
                  <Mail strokeWidth={2} className="w-5 h-5" />
                </div>
                <InputField
                  type={'email'}
                  name="email"
                  isRequired={true}
                  onChange={handleInputChange}
                  value={credentials.email}
                  placeholder="admin@wayride.com"
                  className="pl-12"
                />
              </div>
            </div>

            <div className='space-y-2'>
              <div className="flex items-center justify-between">
                <label className='text-sm font-semibold text-surface-700'>Password</label>
                <button type="button" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400">
                  <Lock strokeWidth={2} className="w-5 h-5" />
                </div>
                <InputField
                  type={'password'}
                  name="password"
                  isRequired={true}
                  onChange={handleInputChange}
                  value={credentials.password}
                  placeholder="Enter your password"
                  className="pl-12"
                />
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 rounded border-surface-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="remember" className="text-sm text-surface-600">
                Keep me signed in
              </label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              label="Sign in to Dashboard"
              icon={<ArrowRight strokeWidth={2} className="w-5 h-5" />}
              variant="primary"
              fullWidth
            />
          </form>

          {/* Footer */}
          <div className="mt-10 pt-8 border-t border-surface-100 text-center">
            <p className="text-sm text-surface-400">
              © {new Date().getFullYear()} WayRide. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
